import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { snakeCase } from 'lodash';

import { Configuration, GetOpenLotsRequest, LotApi } from '../_openapi/portfolio-optimizer';
import { processFilter } from '../widgets/filters/filterUtils';
import { defaultApiConfig } from './defaultApiConfig';

const lotApi = new LotApi(new Configuration(defaultApiConfig));

export type LotVariant = 'open' | 'closed';
export const useOpenLots = ({
    pagination,
    sorting,
    columnFilters,
}: {
    pagination?: PaginationState;
    sorting?: SortingState;
    columnFilters?: ColumnFiltersState;
}) =>
    useQuery({
        queryKey: ['openLots', 'list', { pagination, sorting, columnFilters }],
        queryFn: () => {
            const query: GetOpenLotsRequest = {};

            if (pagination) {
                query.pageIndex = pagination.pageIndex;
                query.pageSize = pagination.pageSize;
            }

            if (sorting?.length) {
                query.sortLabel = snakeCase(sorting[0].id);
                query.sortDesc = sorting[0].desc;
            }

            if (columnFilters?.length) {
                query.filter = columnFilters.map(processFilter);
            }

            return lotApi.getOpenLots(query);
        },
        placeholderData: keepPreviousData,
    });

export const useClosedLots = ({
    pagination,
    sorting,
    columnFilters,
    enabled = true,
}: {
    pagination?: PaginationState;
    sorting?: SortingState;
    columnFilters?: ColumnFiltersState;
    enabled?: boolean;
}) =>
    useQuery({
        enabled,
        queryKey: ['closedLots', 'list', { pagination, sorting, columnFilters }],
        queryFn: () => {
            const query: GetOpenLotsRequest = {};

            if (pagination) {
                query.pageIndex = pagination.pageIndex;
                query.pageSize = pagination.pageSize;
            }

            if (sorting?.length) {
                query.sortLabel = snakeCase(sorting[0].id);
                query.sortDesc = sorting[0].desc;
            }

            if (columnFilters?.length) {
                query.filter = columnFilters.map(processFilter);
            }

            return lotApi.getClosedLots(query);
        },
        placeholderData: keepPreviousData,
    });
